import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './dashboard.css';
import TicketTableDashboard from './TicketTableDashboard';
import TicketTableDashboardExperience from './TicketTableDashboardExperience';
import { useTranslation } from 'react-i18next';
import { 
    FaTicketAlt, 
    FaRobot, 
    FaBoxOpen, 
    FaShoppingCart, 
    FaChartLine,
    FaExternalLinkAlt,
    FaExclamationCircle,
    FaUsers,
    FaTrophy,
    FaSmile,
    FaMoneyBillWave,
    FaExclamationTriangle,
    FaBox,
    FaPlusCircle
} from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const [user, setUser] = useState(undefined);
    const [tickets, setTickets] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [allTickets, setAllTickets] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [monthlyUsage, setMonthlyUsage] = useState({ tickets_used: 0, ticket_limit: 0, extra_tickets_purchased: 0, remaining_extra_tickets: 0 });
    const [ticketStats, setTicketStats] = useState({
        total_tickets: 0,
        ai_resolved_tickets: 0,
        assigned_tickets: 0,
        open_tickets: 0,
        pending_tickets: 0,
        closed_tickets: 0,
        resolved_tickets: 0,
        automation_ratio: 0,
        automated_tickets: 0,
        remaining_tickets: 0,
        waiting_carrier_response: 0,
        new_tickets: 0,
        affected_tickets: 0,
        waiting_client_response: 0,
        user_type: ''
    });
    const [orders, setOrders] = useState([]);
    const [returns, setReturns] = useState([]);
    const [attentionProducts, setAttentionProducts] = useState([]);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [tagsStats, setTagsStats] = useState(null);
    const [clientStats, setClientStats] = useState({
        total_clients: 0,
        total_tickets: 0,
        most_active_client: null,
        clients: []
    });
    const [isStatsCardCollapsed, setIsStatsCardCollapsed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [shopStats, setShopStats] = useState(null);
    const [isShopConfigured, setIsShopConfigured] = useState(true);

    // Brand-related state
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);

    // State for the new ticket creation overlay
    const [isCreateTicketOverlayOpen, setIsCreateTicketOverlayOpen] = useState(false);
    const [newTicketData, setNewTicketData] = useState({ tracking: '', message: '', ticket_topic_id: '' });
    const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
    const [submitTicketError, setSubmitTicketError] = useState(null);
    const [submitTicketSuccess, setSubmitTicketSuccess] = useState(null);

    // State for OTP change overlay
    const [isOtpChangeOverlayOpen, setIsOtpChangeOverlayOpen] = useState(false);
    const [newOtpCode, setNewOtpCode] = useState('');
    const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
    const [submitOtpError, setSubmitOtpError] = useState(null);
    const [submitOtpSuccess, setSubmitOtpSuccess] = useState(null);

    // Dummy data for experience account - updated subjects
    const dummyTickets = [
        { _id: '1', channel_name: 'Instagram', subject: 'Question about T-Shirt material', sentiment_score: 60, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'no', status: 'new', updated_at: new Date().toISOString(), external_ticket_id: 'EXP001', metadata: { reference: 'REF001' } },
        { _id: '2', channel_name: 'Messenger', subject: 'Product damaged on arrival (Mug)', sentiment_score: 85, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'yes', status: 'open', updated_at: new Date(Date.now() - 3600 * 1000 * 24).toISOString(), external_ticket_id: 'EXP002', metadata: { reference: 'REF002' }  },
        { _id: '3', channel_name: 'WhatsApp', subject: 'Inquiry about bulk order discount for Hoodies', sentiment_score: 70, carrier_name: 'Carrier C', company_name: 'Test Inc.', employee_assigned: 'no', status: 'pending', updated_at: new Date(Date.now() - 3600 * 1000 * 48).toISOString(), external_ticket_id: 'EXP003', metadata: { reference: 'REF003' }  },
        { _id: '4', channel_name: 'Gmail', subject: 'Cancelling my monthly sock subscription', sentiment_score: 30, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'yes', status: 'resolved', updated_at: new Date(Date.now() - 3600 * 1000 * 72).toISOString(), external_ticket_id: 'EXP004', metadata: { reference: 'REF004' }  },
        { _id: '5', channel_name: 'Website', subject: 'Inquiry about dimensions for Voyager Backpack', sentiment_score: 75, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'no', status: 'closed', updated_at: new Date(Date.now() - 3600 * 1000 * 96).toISOString(), external_ticket_id: 'EXP005', metadata: { reference: 'REF005' }  },
        { _id: '6', channel_name: 'Instagram', subject: 'Issue with Galaxy T-shirt color fading', sentiment_score: 15, carrier_name: 'Carrier C', company_name: 'Test Inc.', employee_assigned: 'yes', status: 'new', updated_at: new Date(Date.now() - 3600 * 1000 * 5).toISOString(), external_ticket_id: 'EXP006', metadata: { reference: 'REF006' }  },
        { _id: '7', channel_name: 'Messenger', subject: 'Question about return policy for electronics', sentiment_score: 65, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'no', status: 'open', updated_at: new Date(Date.now() - 3600 * 1000 * 10).toISOString(), external_ticket_id: 'EXP007', metadata: { reference: 'REF007' }  },
        { _id: '8', channel_name: 'WhatsApp', subject: 'Late delivery complaint for order #EXP008', sentiment_score: 20, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'yes', status: 'pending', updated_at: new Date(Date.now() - 3600 * 1000 * 15).toISOString(), external_ticket_id: 'EXP008', metadata: { reference: 'REF008' }  },
        { _id: '9', channel_name: 'Gmail', subject: 'Request for gift wrapping option for Astro Mug', sentiment_score: 75, carrier_name: 'Carrier C', company_name: 'Test Inc.', employee_assigned: 'no', status: 'resolved', updated_at: new Date(Date.now() - 3600 * 1000 * 20).toISOString(), external_ticket_id: 'EXP009', metadata: { reference: 'REF009' }  },
        { _id: '10', channel_name: 'Website', subject: 'Incorrect price listed for Astronaut Mug', sentiment_score: 50, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'yes', status: 'closed', updated_at: new Date(Date.now() - 3600 * 1000 * 25).toISOString(), external_ticket_id: 'EXP010', metadata: { reference: 'REF010' }  },
        { _id: '11', channel_name: 'Instagram', subject: 'Question about washing instructions for Nebula Hoodie', sentiment_score: 62, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'no', status: 'new', updated_at: new Date(Date.now() - 3600 * 1000 * 30).toISOString(), external_ticket_id: 'EXP011', metadata: { reference: 'REF011' }  },
        { _id: '12', channel_name: 'Messenger', subject: 'Compliment for fast shipping of my order', sentiment_score: 98, carrier_name: 'Carrier C', company_name: 'Test Inc.', employee_assigned: 'yes', status: 'open', updated_at: new Date(Date.now() - 3600 * 1000 * 35).toISOString(), external_ticket_id: 'EXP012', metadata: { reference: 'REF012' }  },
        { _id: '13', channel_name: 'WhatsApp', subject: 'Inquiry about shipping Rocket Cap to Canada', sentiment_score: 60, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'no', status: 'pending', updated_at: new Date(Date.now() - 3600 * 1000 * 40).toISOString(), external_ticket_id: 'EXP013', metadata: { reference: 'REF013' }  },
        { _id: '14', channel_name: 'Gmail', subject: 'Request for invoice copy for order #EXP014', sentiment_score: 55, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'yes', status: 'resolved', updated_at: new Date(Date.now() - 3600 * 1000 * 45).toISOString(), external_ticket_id: 'EXP014', metadata: { reference: 'REF014' }  },
        { _id: '15', channel_name: 'Website', subject: 'Is the Stainless Steel Water Bottle BPA-free?', sentiment_score: 70, carrier_name: 'Carrier C', company_name: 'Test Inc.', employee_assigned: 'no', status: 'closed', updated_at: new Date(Date.now() - 3600 * 1000 * 50).toISOString(), external_ticket_id: 'EXP015', metadata: { reference: 'REF015' }  },
        { _id: '16', channel_name: 'Instagram', subject: 'Issue with applying "SAVE10" discount code to Alien Socks', sentiment_score: 35, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'yes', status: 'new', updated_at: new Date(Date.now() - 3600 * 1000 * 55).toISOString(), external_ticket_id: 'EXP016', metadata: { reference: 'REF016' }  },
        { _id: '17', channel_name: 'Messenger', subject: 'How to track my order #EXP017?', sentiment_score: 68, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'no', status: 'open', updated_at: new Date(Date.now() - 3600 * 1000 * 60).toISOString(), external_ticket_id: 'EXP017', metadata: { reference: 'REF017' }  },
        { _id: '18', channel_name: 'WhatsApp', subject: 'Problem with payment for Solar System Poster', sentiment_score: 25, carrier_name: 'Carrier C', company_name: 'Test Inc.', employee_assigned: 'yes', status: 'pending', updated_at: new Date(Date.now() - 3600 * 1000 * 65).toISOString(), external_ticket_id: 'EXP018', metadata: { reference: 'REF018' }  },
        { _id: '19', channel_name: 'Gmail', subject: 'My order #EXP001 (Spaceship Keychain) hasn\'t arrived yet', sentiment_score: 18, carrier_name: 'Carrier A', company_name: 'Experience Co.', employee_assigned: 'no', status: 'resolved', updated_at: new Date(Date.now() - 3600 * 1000 * 70).toISOString(), external_ticket_id: 'EXP019', metadata: { reference: 'REF019' }  },
        { _id: '20', channel_name: 'Website', subject: 'Can I get the Mars Rover Phone Case for iPhone 15?', sentiment_score: 80, carrier_name: 'Carrier B', company_name: 'Demo Corp', employee_assigned: 'yes', status: 'closed', updated_at: new Date(Date.now() - 3600 * 1000 * 75).toISOString(), external_ticket_id: 'EXP020', metadata: { reference: 'REF020' }  }
    ];

    const connectWebSocket = () => {
        const token = sessionStorage.getItem('access_token');
        if (!token) return;

        // Check token expiration before connecting
        try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            if (tokenPayload.exp && tokenPayload.exp * 1000 < Date.now()) {
                console.log('Token has expired, not connecting WebSocket');
                // Clear session and redirect to login if needed
                sessionStorage.removeItem('access_token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('Error parsing token:', error);
            return;
        }

        wsRef.current = new WebSocket(`wss://${window.location.host}/api/ws?token=${token}`);
        
        wsRef.current.onopen = (event) => {
            console.log('WebSocket connection established:', event);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        wsRef.current.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        wsRef.current.onclose = (event) => {
            console.log('WebSocket connection closed:', event);
            console.log('Close code:', event.code);
            console.log('Close reason:', event.reason);
            
            // Check if closure was due to authentication error (typical codes for auth issues)
            if (event.code === 1008 || event.code === 4000 || event.code === 4001 || 
                event.reason?.toLowerCase().includes('token') || 
                event.reason?.toLowerCase().includes('auth')) {
                console.log('WebSocket closed due to authentication issue, not reconnecting');
                // Optionally redirect to login
                sessionStorage.removeItem('access_token');
                navigate('/');
                return;
            }

            if (user?.role !== 'experience') { // Only reconnect if not experience user
                reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
            }
        };

        wsRef.current.onmessage = (event) => {
            console.log('WebSocket message received:', event.data);
            if (event.data === 'ping') {
                wsRef.current.send('pong');
            } else {
                if (user?.role !== 'experience') { // Only fetch real tickets if not experience user
                    // Reset initial load flag and fetch fresh data on WebSocket update
                    setIsInitialLoad(true);
                    setAllTickets([]);
                    fetchTickets(token, 1);
                }
            }
        };
    };

    useEffect(() => {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchAllData = async () => {
            try {
                setIsLoading(true);
                
                const userData = await fetchUserData(token);
                
                if (userData?.role === 'experience') {
                    setTickets(dummyTickets); // Use dummy tickets for experience user
                    // Potentially set other dummy data here if needed for other dashboard sections
                    setMonthlyUsage({ tickets_used: 150, ticket_limit: 200, extra_tickets_purchased: 50, remaining_extra_tickets: 25 });
                    setTicketStats({
                        total_tickets: 20,
                        ai_resolved_tickets: 5,
                        assigned_tickets: 10,
                        open_tickets: 5,
                        pending_tickets: 3,
                        closed_tickets: 7,
                        resolved_tickets: 5,
                        automation_ratio: 0.25,
                        automated_tickets: 5,
                        remaining_tickets: 0,
                        waiting_carrier_response: 2,
                        new_tickets: 3,
                        affected_tickets: 1,
                        waiting_client_response: 4,
                        user_type: 'experience'
                    });
                    setOrders([
                        { id: 'exp_ord_1', order_number: 'EXP1001', customer: { name: 'John Experience' }, total_price: '45.98', financial_status: 'paid' },
                        { id: 'exp_ord_2', order_number: 'EXP1002', customer: { name: 'Jane Demo' }, total_price: '44.99', financial_status: 'pending' },
                    ]);
                    setReturns([
                        { _id: 'expr1', items: [{ title: 'Demo Product A', price: 49.99 }], order_id: 'EXP1001', status: 'approved' },
                    ]);
                    setAttentionProducts([
                        { id: 'exp001', title: 'Experience Item X', health_status: { reasons: ['low_stock', 'low_sales_velocity'], metrics: { sales_30_days: 5 } } },
                    ]);
                    setShopStats({
                        orders: { total_revenue: 12500.75, average_order_value: 85.20, total_orders: 146 },
                        sales_analysis: { average_items_per_order: 2.1 },
                        customers: { total_customers: 95, new_customers_30d: 15 },
                        products: { total_products: 75, total_variants: 250, out_of_stock_count: 3, low_stock_count: 8 }
                    });
                    setIsShopConfigured(true);
                    setIsLoading(false);
                    return;
                }
                
                const promises = [
                    fetchTickets(token, 1),
                    fetchMonthlyUsage(token),
                    fetchTicketStats(token)
                ];
                
                // Fetch brands if user has_brand is true
                if (userData?.has_brand) {
                    console.log('🚀 User has brands, fetching brand list...');
                    promises.push(fetchBrands(token));
                } else {
                    console.log('🚫 User does not have brands (has_brand:', userData?.has_brand, ')');
                }
                
                if (!userData?.edn_api_account) {
                    promises.push(
                        fetchOrders(token),
                        fetchAttentionProducts(),
                        fetchReturns(token),
                        fetchShopStatistics(token)
                    );
                } else {
                    promises.push(fetchTagsStatistics(token));
                }
                
                await Promise.all(promises);
                await fetchClientStats(token);

            } catch (error) {
                console.error('Error loading dashboard data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
        if (user?.role !== 'experience') {
            connectWebSocket();
        }

        const pingInterval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send('ping');
            }
        }, 30000);

        return () => {
            clearInterval(pingInterval);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [navigate, user?.role]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('👤 User data received:', response.data);
            console.log('🏷️ User has_brand:', response.data?.has_brand);
            setUser(response.data);
            if (response.data?._id) {
                sessionStorage.setItem('user_id', response.data._id);
            }
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            return null;
        }
    };

    const fetchMonthlyUsage = async (token) => {
        try {
            const response = await axios.get('/api/users/monthly-usage', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMonthlyUsage(response.data || { tickets_used: 0, ticket_limit: 0, extra_tickets_purchased: 0, remaining_extra_tickets: 0 });
        } catch (error) {
            console.error('Failed to fetch monthly usage:', error);
        }
    };

    const fetchTickets = async (token, page = 1) => {
        try {
            const ticketsPerPage = 25;
            let limit = ticketsPerPage;
            let requestPage = page;

            // On initial load, fetch 3 pages worth of tickets (75 tickets)
            if (isInitialLoad && page === 1) {
                limit = 75; // 3 pages × 25 tickets per page
                requestPage = 1;
            }

            const response = await axios.get(`/api/tickets/tickets?page=${requestPage}&limit=${limit}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                if (isInitialLoad && page === 1) {
                    // Store all 75 tickets for local pagination
                    setAllTickets(response.data.tickets || []);
                    
                    // Set tickets to first page (first 25 tickets)
                    const firstPageTickets = (response.data.tickets || []).slice(0, 25);
                    setTickets(firstPageTickets);
                    
                    // Create modified pagination info for local pagination
                    const originalPagination = response.data.pagination;
                    const modifiedPagination = {
                        ...originalPagination,
                        current_page: 1,
                        has_previous: false,
                        has_next: (response.data.tickets || []).length > 25 || originalPagination?.has_next,
                        // Keep original total_pages since we might have more pages beyond our cached 3
                    };
                    setPaginationInfo(modifiedPagination);
                    setIsInitialLoad(false);
                } else if (page <= 3 && allTickets.length > 0) {
                    // For pages 2-3, use cached data
                    const startIndex = (page - 1) * 25;
                    const endIndex = startIndex + 25;
                    const pageTickets = allTickets.slice(startIndex, endIndex);
                    setTickets(pageTickets);
                    
                    // Update pagination info for local navigation
                    setPaginationInfo(prev => ({
                        ...prev,
                        current_page: page,
                        has_previous: page > 1,
                        has_next: endIndex < allTickets.length || prev?.total_pages > 3,
                    }));
                } else {
                    // For pages beyond 3, fetch from API normally
                    setTickets(response.data.tickets || []);
                    setPaginationInfo(response.data.pagination);
                }
                setCurrentPage(page);
            } else {
                setTickets([]);
                setPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            setTickets([]);
            setPaginationInfo(null);
        }
    };

    const fetchTicketStats = async (token) => {
        try {
            const response = await axios.get('/api/tickets/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('API response:', response.data);
            setTicketStats(response.data || {
                total_tickets: 0,
                ai_resolved_tickets: 0,
                assigned_tickets: 0,
                open_tickets: 0,
                pending_tickets: 0,
                closed_tickets: 0,
                resolved_tickets: 0,
                automation_ratio: 0,
                automated_tickets: 0,
                remaining_tickets: 0,
                waiting_carrier_response: 0,
                new_tickets: 0,
                affected_tickets: 0,
                waiting_client_response: 0,
                user_type: ''
            });
        } catch (error) {
            console.error('Failed to fetch ticket stats:', error);
        }
    };

    const fetchOrders = async (token) => {
        try {
            const response = await axios.get('/api/shopify/get_orders', {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 5 }
            });
            if (response.data.status === "unconfigured") {
                setOrders([]);
            } else {
                setOrders(response.data.orders || []);
            }
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            setOrders([]);
        } 
    };

    const fetchReturns = async (token) => {
        try {
            const response = await axios.get('/api/webhook/returns', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const returnsData = response.data.returns || [];
            setReturns(returnsData.slice(0, 5));
        } catch (error) {
            console.error('Failed to fetch returns:', error);
            setReturns([]);
        }
    };

    const fetchAttentionProducts = async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.get('/api/shopify/get_products_needing_attention', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status === "unconfigured" || !response.data.products) {
                setAttentionProducts([]);
            } else {
                setAttentionProducts(response.data.products.slice(0, 5)); // Only get top 5
            }
        } catch (error) {
            console.error('Failed to fetch attention products:', error);
            setAttentionProducts([]);
        }
    };

    const fetchTagsStatistics = async (token) => {
        try {
            const response = await axios.get('/api/tickets/tickets-tags-stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTagsStats(response.data);
        } catch (error) {
            console.error('Error fetching tags statistics:', error);
        }
    };

    const fetchClientStats = async (token) => {
        try {
            const response = await axios.get('/api/tickets/client-stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClientStats(response.data);
        } catch (error) {
            console.error('Failed to fetch client stats:', error);
        }
    };

    const fetchShopStatistics = async (token) => {
        try {
            const response = await axios.get('/api/shopify/get_shop_statistics', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.status === "unconfigured") {
                setIsShopConfigured(false);
                setShopStats(null);
            } else {
                setIsShopConfigured(true);
                setShopStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch shop statistics:', error);
            setShopStats(null);
            setIsShopConfigured(false);
        }
    };

    const fetchBrands = async (token) => {
        console.log('🔍 fetchBrands called');
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ Brand API response:', response.data);
            setBrands(response.data || []);
            // Set the first brand as default selected if brands exist
            if (response.data && response.data.length > 0) {
                console.log('🎯 Setting selected brand to:', response.data[0].id);
                setSelectedBrand(response.data[0].id);
            } else {
                console.log('❌ No brands found in response');
            }
        } catch (error) {
            console.error('❌ Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const handleTicketClick = async (ticketId) => {
        try {
            const token = sessionStorage.getItem('access_token');
            if (user?.role !== 'experience') { // Only make API call if not experience user
                await axios.put(`/api/tickets/${ticketId}/status`, null, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            navigate(`/tickets/${ticketId}/messages`);
        } catch (error) {
            console.error('Failed to update ticket status:', error);
        }
    };

    const toggleCard = (cardId) => {
        setExpandedCards(prev => ({
            ...prev,
            [cardId]: !prev[cardId]
        }));
    };

    const formatReason = (reason) => {
        const reasons = {
            out_of_stock: 'Out of Stock',
            low_stock: 'Low Stock',
            overstock: 'Overstock',
            low_sales_velocity: 'Low Sales',
            high_discount: 'High Discount',
            missing_handle: 'SEO Issue',
            thin_content: 'Thin Content',
            no_images: 'No Images',
            few_images: 'Few Images',
            no_tags: 'No Tags',
            single_variant: 'Single Variant'
        };
        return reasons[reason] || reason;
    };

    const tagsChartData = tagsStats?.tags_statistics ? {
        labels: tagsStats.tags_statistics.map(item => item._id),
        datasets: [{
            data: tagsStats.tags_statistics.map(item => item.percentage),
            backgroundColor: [
                '#00ff9d', '#00ffff', '#ff00ff', 
                '#8000ff', '#ffff00', '#ff0000', '#0099ff'
            ],
        }],
    } : null;

    const toggleStatsCard = () => {
        setIsStatsCardCollapsed(!isStatsCardCollapsed);
    };

    // Handlers for the create ticket overlay
    const handleOpenCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
    };

    const handleCloseCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(false);
    };

    const handleNewTicketInputChange = (e) => {
        const { name, value } = e.target;
        setNewTicketData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitNewTicket = async (e) => {
        e.preventDefault();
        setIsSubmittingTicket(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        const token = sessionStorage.getItem('access_token');

        if (user?.role === 'experience') {
            // Simulate API call for experience user
            setTimeout(() => {
                setSubmitTicketSuccess(t('ticketCreatedSuccessfully', 'Ticket created successfully!') + ` ID: EXP${Math.floor(Math.random() * 1000)}`);
                setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
                setIsSubmittingTicket(false);
                setTimeout(() => {
                    handleCloseCreateTicketOverlay();
                }, 2500);
            }, 1000);
            return;
        }

        if (!newTicketData.ticket_topic_id || isNaN(parseInt(newTicketData.ticket_topic_id, 10))) {
            setSubmitTicketError(t('ticketTopicIdMustBeNumber', 'Ticket Topic ID must be a valid number.'));
            setIsSubmittingTicket(false);
            return;
        }

        const payload = {
            ...newTicketData,
            ticket_topic_id: parseInt(newTicketData.ticket_topic_id, 10)
        };

        try {
            const response = await axios.post('/api/tickets/forward_to_envoidunet_create', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubmitTicketSuccess(t('ticketCreatedSuccessfully', 'Ticket created successfully!') + (response.data?.ticket_id ? ` ID: ${response.data.ticket_id}` : ''));
            setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
            setTimeout(() => {
                handleCloseCreateTicketOverlay();
            }, 2500);
        } catch (error) {
            console.error('Failed to create EDN ticket:', error);
            setSubmitTicketError(error.response?.data?.detail || t('failedToCreateEdnTicket', 'Failed to create ticket. Please try again.'));
        } finally {
            setIsSubmittingTicket(false);
        }
    };

    // Handlers for OTP change overlay
    const handleOpenOtpChangeOverlay = () => {
        setIsOtpChangeOverlayOpen(true);
        setSubmitOtpError(null);
        setSubmitOtpSuccess(null);
        setNewOtpCode('');
    };

    const handleCloseOtpChangeOverlay = () => {
        setIsOtpChangeOverlayOpen(false);
        setNewOtpCode('');
        setSubmitOtpError(null);
        setSubmitOtpSuccess(null);
    };

    const handleSubmitOtpChange = async (e) => {
        e.preventDefault();
        setIsSubmittingOtp(true);
        setSubmitOtpError(null);
        setSubmitOtpSuccess(null);
        
        const token = sessionStorage.getItem('access_token');

        if (user?.role === 'experience') {
            // Simulate API call for experience user
            setTimeout(() => {
                setSubmitOtpSuccess(t('otpUpdatedSuccessfully', 'OTP code updated successfully!'));
                setNewOtpCode('');
                setIsSubmittingOtp(false);
                setTimeout(() => {
                    handleCloseOtpChangeOverlay();
                }, 2500);
            }, 1000);
            return;
        }

        if (!newOtpCode.trim()) {
            setSubmitOtpError(t('otpCodeRequired', 'OTP code is required.'));
            setIsSubmittingOtp(false);
            return;
        }

        try {
            await axios.put('/api/users/user/otp', 
                { otp_code: newOtpCode },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSubmitOtpSuccess(t('otpUpdatedSuccessfully', 'OTP code updated successfully!'));
            setNewOtpCode('');
            setTimeout(() => {
                handleCloseOtpChangeOverlay();
            }, 2500);
        } catch (error) {
            console.error('Failed to update OTP code:', error);
            setSubmitOtpError(
                error.response?.data?.detail || 
                t('failedToUpdateOtp', 'Failed to update OTP code. Please try again.')
            );
        } finally {
            setIsSubmittingOtp(false);
        }
    };

    const handleBrandChange = (brandId) => {
        console.log('🔄 Brand changed to:', brandId);
        setSelectedBrand(brandId);
    };

    const handlePageChange = (newPage) => {
        const token = sessionStorage.getItem('access_token');
        if (token && newPage > 0 && newPage <= (paginationInfo?.total_pages || 1)) {
            // For pages 1-3, use local pagination with cached data
            if (newPage <= 3 && allTickets.length > 0) {
                const startIndex = (newPage - 1) * 25;
                const endIndex = startIndex + 25;
                const pageTickets = allTickets.slice(startIndex, endIndex);
                
                setTickets(pageTickets);
                setCurrentPage(newPage);
                
                // Update pagination info for local navigation
                setPaginationInfo(prev => ({
                    ...prev,
                    current_page: newPage,
                    has_previous: newPage > 1,
                    has_next: endIndex < allTickets.length || prev?.total_pages > 3,
                }));
            } else {
                // For pages beyond 3, fetch from API
                fetchTickets(token, newPage);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="una-dashboard-loading-screen">
                <div className="una-loading-content">
                    <div className="una-loading-logo">
                        <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
                    </div>
                    <h2>{t('loadingDashboard')}</h2>
                    <div className="una-loading-progress">
                        <div className="una-progress-bar">
                            <div className="una-progress-glow"></div>
                        </div>
                    </div>
                    <div className="una-loading-metrics">
                        <div className="una-metric-item">
                            <FaTicketAlt />
                            <span>{t('loadingTickets')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaShoppingCart />
                            <span>{t('loadingOrders')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaChartLine />
                            <span>{t('loadingStats')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaBoxOpen />
                            <span>{t('loadingReturns')}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="una-dashboard-container">
            {/* OTP Change Button - Top Right Corner */}
            {user?.fa_account && (
                <div className="una-otp-change-button-container">
                    <button 
                        className="una-otp-change-button"
                        onClick={handleOpenOtpChangeOverlay}
                        title={t('changeOtpCode', 'Change OTP Code')}
                    >
                        <FaRobot />
                        {t('changeOtp', 'Change OTP')}
                    </button>
                </div>
            )}

            {/* Only show stats header for edn_api_account users and experience users, but no button */}
            {(user?.edn_api_account || user?.role === 'experience') && (
                <div className="una-dashboard-header">
                    <div className="una-dashboard-stats">
                        <div className="una-stats-card">
                            {!isStatsCardCollapsed && (
                                <div className="una-stats-card-content">
                                    <div className="una-stats-section">
                                        <div className="una-stats-grid">
                                            <div className="una-metric-item primary">
                                                <div className="una-metric-icon primary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value primary">{ticketStats.new_tickets}</span>
                                                    <span className="una-metric-label">{t('newTickets')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item secondary">
                                                <div className="una-metric-icon secondary">
                                                    <FaRobot />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value secondary">{ticketStats.automated_tickets}</span>
                                                    <span className="una-metric-label">{t('automatedTickets')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item secondary">
                                                <div className="una-metric-icon secondary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value secondary">{ticketStats.waiting_client_response}</span>
                                                    <span className="una-metric-label">{t('waitingClientResponse')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item tertiary">
                                                <div className="una-metric-icon tertiary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value tertiary">{ticketStats.waiting_carrier_response}</span>
                                                    <span className="una-metric-label">{t('waitingCarrierResponse')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item tertiary">
                                                <div className="una-metric-icon tertiary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value tertiary">{ticketStats.resolved_tickets}</span>
                                                    <span className="una-metric-label">{t('resolvedTickets')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item tertiary">
                                                <div className="una-metric-icon tertiary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value tertiary">{ticketStats.closed_tickets}</span>
                                                    <span className="una-metric-label">{t('closedTickets')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="una-dashboard-main">
                {(user?.edn_api_account || user?.role === 'admin') ? (
                    user?.role === 'experience' ? (
                        <TicketTableDashboardExperience
                            tickets={tickets} // This will be dummyTickets if role is 'experience'
                            user={user}
                            onTicketClick={handleTicketClick}
                        />
                    ) : (
                        <TicketTableDashboard 
                            tickets={tickets} 
                            user={user}
                            onTicketClick={handleTicketClick}
                            hasFaAccount={user?.fa_account}
                            onCreateTicket={handleOpenCreateTicketOverlay}
                            brands={brands}
                            selectedBrand={selectedBrand}
                            onBrandChange={handleBrandChange}
                            pagination={paginationInfo}
                            onPageChange={handlePageChange}
                        />
                    )
                ) : !isShopConfigured ? (
                    <div className="una-unconfigured-shop">
                        <div className="una-welcome-message">
                            <FaShoppingCart className="una-welcome-icon" />
                            <h2>{t('welcomeToUnanimity')}</h2>
                            <p>{t('configureShopMessage')}</p>
                            <button 
                                className="una-primary-button"
                                onClick={() => navigate('/parameters')}
                            >
                                {t('configureShop')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="una-business-dashboard">
                        {/* Shop Statistics Overview */}
                        {shopStats && (
                            <div className="una-store-overview">
                                <div className="una-stats-grid">
                                    <div className="una-stat-card revenue">
                                        <FaMoneyBillWave />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.totalRevenue')}</h3>
                                            <p>${shopStats.orders?.total_revenue?.toFixed(2) || '0.00'}</p>
                                            <span>{t('shopStats.avgOrder')}: ${shopStats.orders?.average_order_value?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card orders">
                                        <FaShoppingCart />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.totalOrders')}</h3>
                                            <p>{shopStats.orders?.total_orders || 0}</p>
                                            <span>{t('shopStats.avgItems')}: {shopStats.sales_analysis?.average_items_per_order?.toFixed(1) || '0.0'}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card customers">
                                        <FaUsers />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.customers')}</h3>
                                            <p>{shopStats.customers?.total_customers || 0}</p>
                                            <span>{t('shopStats.newCustomers')}: {shopStats.customers?.new_customers_30d || 0}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card products">
                                        <FaBox />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.products')}</h3>
                                            <p>{shopStats.products?.total_products || 0}</p>
                                            <span>{t('shopStats.variants')}: {shopStats.products?.total_variants || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Alerts */}
                                <div className="una-alerts-section">
                                    {shopStats.products?.out_of_stock_count > 0 && (
                                        <div className="una-alert out-of-stock">
                                            <FaExclamationTriangle />
                                            <span>{shopStats.products.out_of_stock_count} {t('shopStats.outOfStock')}</span>
                                        </div>
                                    )}
                                    {shopStats.products?.low_stock_count > 0 && (
                                        <div className="una-alert low-stock">
                                            <FaExclamationTriangle />
                                            <span>{shopStats.products.low_stock_count} {t('shopStats.lowStock')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Main dashboard grid with key information - now with 3 cards instead of 4 */}
                        <div className="una-dashboard-grid">
                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaShoppingCart className="orders-icon" />
                                    <h3>{t('recentOrders')}</h3>
                                    <button onClick={() => navigate('/orders')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {orders.slice(0, 5).map(order => (
                                        <div key={order.id} className="una-compact-item" onClick={() => navigate(`/order/${order.id}`)}>
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">#{order.order_number}</span>
                                                <span className="una-secondary-text">{order.customer?.name}</span>
                                            </div>
                                            <div className="una-compact-details">
                                                <span className="una-price-tag">${order.total_price}</span>
                                                <span className={`una-status-badge-dashboard ${order.financial_status?.toLowerCase()}`}>
                                                    {order.financial_status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaBoxOpen className="returns-icon" />
                                    <h3>{t('recentReturns')}</h3>
                                    <button onClick={() => navigate('/returns')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {returns.slice(0, 5).map(returnItem => (
                                        <div 
                                            key={returnItem._id} 
                                            className="una-compact-item"
                                            onClick={() => navigate(`/return/${returnItem._id}`)}
                                        >
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">{returnItem.items?.[0]?.title || 'No title'}</span>
                                                <span className="una-secondary-text">#{returnItem.order_id}</span>
                                            </div>
                                            <div className="una-compact-details">
                                                <span className="una-price-tag">
                                                    €{(returnItem.items?.[0]?.price !== undefined ? 
                                                        Number(returnItem.items[0].price).toFixed(2) : 
                                                        '0.00')}
                                                </span>
                                                <span className={`una-status-badge-dashboard ${returnItem.status.replace('_', ' ').replace(' ', '-').toLowerCase()}`}>
                                                    {returnItem.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        
                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaExclamationCircle className="attention-icon" />
                                    <h3>{t('productsNeedingAttention')}</h3>
                                    <button onClick={() => navigate('/shop-statistics')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {attentionProducts.slice(0, 5).map(product => (
                                        <div 
                                            key={product.id} 
                                            className="una-compact-item"
                                            onClick={() => navigate(`/product/${product.id}`)}
                                        >
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">{product.title}</span>
                                                <div className="una-compact-tags">
                                                    {product.health_status?.reasons?.slice(0, 2).map((reason, index) => (
                                                        <span key={index} className={`una-reason-tag ${reason}`}>
                                                            {formatReason(reason)}
                                                        </span>
                                                    ))}
                                                    {product.health_status?.reasons?.length > 2 && (
                                                        <span className="una-more-tag">+{product.health_status.reasons.length - 2}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="una-compact-details">
                                                {product.health_status?.metrics?.sales_30_days !== undefined && (
                                                    <span className="una-metric-tag">
                                                        <FaShoppingCart />
                                                        {product.health_status.metrics.sales_30_days}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Ticket table shown after the main dashboard */}
                        <div className="una-tickets-section">
                            <div className="una-section-header">
                                <FaTicketAlt className="tickets-icon" />
                                <h2>{t('supportTickets')}</h2>
                            </div>
                            {user?.role === 'experience' ? (
                                <TicketTableDashboardExperience 
                                    tickets={tickets} // dummyTickets
                                    user={user}
                                    onTicketClick={handleTicketClick}
                                    compact={true}
                                />
                            ) : (
                                <TicketTableDashboard 
                                    tickets={tickets} 
                                    user={user}
                                    onTicketClick={handleTicketClick}
                                    compact={true}
                                    hasFaAccount={user?.fa_account}
                                    onCreateTicket={handleOpenCreateTicketOverlay}
                                    brands={brands}
                                    selectedBrand={selectedBrand}
                                    onBrandChange={handleBrandChange}
                                    pagination={paginationInfo}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Overlay for creating EDN ticket */}
            {isCreateTicketOverlayOpen && (
                <div className="una-overlay" onClick={handleCloseCreateTicketOverlay}>
                    <div className="una-overlay-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('createNewEdnTicketTitle', 'Create New EnvoiDuNet Ticket')}</h2>
                        <form onSubmit={handleSubmitNewTicket}>
                            <div className="una-form-group">
                                <label htmlFor="tracking">{t('trackingNumber', 'Tracking Number')}</label>
                                <input 
                                    type="text" 
                                    id="tracking" 
                                    name="tracking" 
                                    value={newTicketData.tracking}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                />
                            </div>
                            <div className="una-form-group">
                                <label htmlFor="message">{t('message', 'Message')}</label>
                                <textarea 
                                    id="message" 
                                    name="message" 
                                    value={newTicketData.message}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                    rows="4"
                                />
                            </div>
                            <div className="una-form-group">
                                <label htmlFor="ticket_topic_id">{t('ticketTopicId', 'Ticket Topic ID')}</label>
                                <input 
                                    type="number" 
                                    id="ticket_topic_id" 
                                    name="ticket_topic_id"
                                    value={newTicketData.ticket_topic_id}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                />
                            </div>
                            {submitTicketError && <p className="una-error-message">{submitTicketError}</p>}
                            {submitTicketSuccess && <p className="una-success-message">{submitTicketSuccess}</p>}
                            <div className="una-form-actions">
                                <button type="button" onClick={handleCloseCreateTicketOverlay} className="una-button una-button-secondary">
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button type="submit" className="una-button una-button-primary" disabled={isSubmittingTicket}>
                                    {isSubmittingTicket ? t('submitting', 'Submitting...') : t('createTicket', 'Create Ticket')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* OTP Change Overlay */}
            {isOtpChangeOverlayOpen && (
                <div className="una-overlay" onClick={handleCloseOtpChangeOverlay}>
                    <div className="una-overlay-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('changeOtpCodeTitle', 'Change OTP Code')}</h2>
                        <form onSubmit={handleSubmitOtpChange}>
                            <div className="una-form-group">
                                <label htmlFor="newOtpCode">{t('newOtpCode', 'New OTP Code')}</label>
                                <input 
                                    type="text" 
                                    id="newOtpCode" 
                                    name="newOtpCode" 
                                    value={newOtpCode}
                                    onChange={(e) => setNewOtpCode(e.target.value)}
                                    placeholder={t('enterNewOtpCode', 'Enter your new OTP code')}
                                    required 
                                />
                                <small className="una-form-help">
                                    {t('otpCodeHelp', 'Enter the new OTP code from your authenticator app')}
                                </small>
                            </div>
                            {submitOtpError && <p className="una-error-message">{submitOtpError}</p>}
                            {submitOtpSuccess && <p className="una-success-message">{submitOtpSuccess}</p>}
                            <div className="una-form-actions">
                                <button 
                                    type="button" 
                                    onClick={handleCloseOtpChangeOverlay} 
                                    className="una-button una-button-secondary"
                                >
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button 
                                    type="submit" 
                                    className="una-button una-button-primary" 
                                    disabled={isSubmittingOtp}
                                >
                                    {isSubmittingOtp ? 
                                        t('updating', 'Updating...') : 
                                        t('updateOtp', 'Update OTP')
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;